/**
 * The general "ask anything" panel.
 *
 * The one thing it must never do is lose what somebody typed. On a failed ask the
 * question stays exactly where it is, the box takes focus, and the error says what
 * happened — nobody types a question, watches it vanish, and has to retype it.
 *
 * Stateless by design: one question, one answer. No history is kept or shown.
 */
import { useId, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { ask, explain } from './assistantApi';
import { Button, ExampleChip, Note, Thinking, cx, textArea } from './ui';

/** Matches MAX_PROMPT in worker/routes/assistant.ts. The Worker is what enforces it. */
const MAX_PROMPT = 4000;

const EXAMPLES = [
  'A fun 20-minute activity for a rainy afternoon',
  'Dinner ideas with chicken, rice, and broccoli',
  'Explain photosynthesis to a 3rd grader',
  'A thoughtful, low-cost birthday surprise',
];

export default function AskPanel() {
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const boxRef = useRef<HTMLTextAreaElement>(null);
  const fieldId = useId();

  const trimmed = question.trim();
  const tooLong = trimmed.length > MAX_PROMPT;
  const canAsk = trimmed.length > 0 && !tooLong && !busy;

  async function submit(e?: FormEvent) {
    e?.preventDefault();
    if (!canAsk) {
      if (!trimmed) setError('Type a question first.');
      return;
    }

    setBusy(true);
    setError(null);
    setAnswer(null); // the previous answer was for a different question

    try {
      const reply = await ask(trimmed);
      setAnswer(reply.answer);
    } catch (err) {
      // The question is never cleared, so it is still on screen. Say what happened.
      setError(explain(err, 'The assistant couldn’t answer just now.'));
      boxRef.current?.focus();
    } finally {
      setBusy(false);
    }
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    // Cmd/Ctrl + Enter asks. Plain Enter stays a newline — a kid writing a couple of
    // lines shouldn't fire off half a question.
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      void submit();
    }
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <div>
        <label htmlFor={fieldId} className="block text-sm font-medium text-ink">
          Ask anything
        </label>
        <p id={`${fieldId}-hint`} className="mt-1 text-sm leading-relaxed text-ink-soft">
          Homework help, dinner ideas, a plan for the weekend — the whole family can use it.
        </p>
        <textarea
          id={fieldId}
          ref={boxRef}
          rows={3}
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={onKeyDown}
          aria-describedby={`${fieldId}-hint`}
          aria-invalid={tooLong || undefined}
          placeholder="Type your question…"
          className={cx('mt-3', textArea, tooLong && 'border-over')}
        />
      </div>

      {!answer && !busy && (
        <div className="flex flex-wrap gap-2">
          {EXAMPLES.map((ex) => (
            <ExampleChip
              key={ex}
              onClick={() => {
                setQuestion(ex);
                setError(null);
                boxRef.current?.focus();
              }}
            >
              {ex}
            </ExampleChip>
          ))}
        </div>
      )}

      {tooLong && (
        <Note tone="warn" role="alert">
          That’s a bit long — trim it to {MAX_PROMPT.toLocaleString()} characters or fewer.
        </Note>
      )}
      {error && (
        <Note tone="warn" role="alert">
          {error} Your question is still here — try again.
        </Note>
      )}

      <div className="flex justify-end">
        <Button type="submit" variant="primary" disabled={!canAsk}>
          {busy ? 'Thinking…' : 'Ask'}
        </Button>
      </div>

      {busy && (
        <div className="pt-1">
          <Thinking />
        </div>
      )}

      {answer && !busy && (
        <div className="rounded-2xl border border-border bg-surface-sunk p-4">
          <h3 className="sr-only">Answer</h3>
          <p className="whitespace-pre-wrap text-base leading-relaxed text-ink">{answer}</p>
          <p className="mt-3 text-xs text-ink-faint">
            The assistant can be wrong — double-check anything important.
          </p>
        </div>
      )}
    </form>
  );
}
